import { Injectable } from '@angular/core';
import {BasicDownloadProgress, DownloadProgress, FileDocument, StorageFile} from './file.model';
import {BehaviorSubject, from, Observable, of, Subject} from 'rxjs';
import {AngularFireStorage} from '@angular/fire/compat/storage';
import {filter, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import * as Papa from 'papaparse';
import {ParseError, ParseResult} from 'papaparse';
import firebase from 'firebase/compat/app';
import {HttpClient, HttpEvent} from '@angular/common/http';
import {LoadRosterSyncReview, RosterSyncJobReview} from '../../staged-analysis/interfaces/roster-sync-job-review.model';

interface LSPParseError extends Omit<ParseError, 'code'> {
  code: ParseError['code'] | 'EmptyFile' | 'MissingColumn' | 'DuplicateRows' | 'FileSize';
}

@Injectable({
  providedIn: 'root'
})
export class FileCache {

  static noDataError: Partial<LSPParseError> = {
    code: 'EmptyFile',
    message: 'This file contains no data'
  };

  static missingColumnError: Partial<LSPParseError> = {
    code: 'MissingColumn',
    message: 'File is missing a column'
  };

  static duplicateRowsError: Partial<LSPParseError> = {
    code: 'DuplicateRows',
    message: 'File contains %s duplicate rows'
  };

  static MAX_ALLOWABLE_BYTES = (150 * 1000000); // 100 megabytes

  static fileSizeError: Partial<LSPParseError> = {
    code: 'FileSize',
    message: `File exceeds maximum allowable size ${FileCache.MAX_ALLOWABLE_BYTES / 1000000} Mb`
  };

  fileDownloadProgress$: Subject<DownloadProgress> = new Subject();

  private store_: {
    [key: string]: {
      observable: Observable<DownloadProgress>;
      timerId?: number;
    };
  };
  private cacheWorker: Worker;
  private workerResponse: Subject<any> = new Subject<any>();
  private customerId: string;

  constructor(private storage: AngularFireStorage, private http: HttpClient) {
    this.store_ = {};
    this.cacheWorker = new Worker(new URL('../webworkers/filecache.worker', import.meta.url), {type: 'module', name: 'file-cache'});
    this.cacheWorker.onmessage = ({data}) => {
      this.workerResponse.next(data);
    };
  }

  private static assignChunkToProgress_(results: ParseResult<any>, progress: DownloadProgress): DownloadProgress {
    if (results.errors && results.errors.length) {
      progress.file.errors.push(...results.errors);
    }
    progress.file.columns = results.meta.fields;
    if (progress.data?.length) {
      progress.data = progress.data.concat(results.data);
    } else {
      progress.data = results.data;
    }
    progress.meta = results.meta;
    return progress;
  }

  private static processChunk_(results: ParseResult<any>, progress: DownloadProgress, rowSet: Set<string>): DownloadProgress {
    results.data.forEach((dataRow) => {
      const str = JSON.stringify(dataRow);
      if (rowSet.has(str)) {
        if (!progress.file.duplicateRows) {
          progress.file.duplicateRows = {};
        }
        if (!progress.file.duplicateRows[str]) {
          progress.file.duplicateRows[str] = 1;
        } else {
          progress.file.duplicateRows[str]++;
        }
      } else {
        rowSet.add(str);
      }
    });
    return FileCache.assignChunkToProgress_(results, progress);
  }

  private static completeParse_(progress: DownloadProgress): DownloadProgress {
    if (progress.file.duplicateRows) {
      const totalDuplicates = Object.values(progress.file.duplicateRows).reduce((total, rowCount) => {
        total += rowCount;
        return total;
      }, 0);
      if (totalDuplicates) {
        const err = (Object.assign({}, FileCache.duplicateRowsError) as ParseError);
        err.message = err.message.replace('%s', totalDuplicates.toString());
        progress.file.errors = progress.file.errors.filter((e) => e.code !== FileCache.duplicateRowsError.code);
        progress.file.errors.push(err);
      } else {
        delete progress.file.duplicateRows;
      }
    }
    progress.complete = true;
    if (!progress.data?.length) {
      progress.file.errors.push(FileCache.noDataError as ParseError);
    }
    if (progress.file.size > FileCache.MAX_ALLOWABLE_BYTES) {
      const fileSizeError = Object.assign({}, FileCache.fileSizeError, {value: progress.file.size}) as ParseError;
      progress.file.errors.push(fileSizeError);
    }
    progress.success = !progress.file.errors.length;
    return progress;
  }

  setCustomerId(id: string): void {
    this.customerId = id;
  }

  getDownloadUrl(fileName: string): Observable<string> {
    return this.storage.ref(`${this.customerId}/${fileName}`).getDownloadURL();
  }

  addObservableToCache(o: BehaviorSubject<DownloadProgress>): Observable<DownloadProgress> {
    const cachedObservable = o.asObservable().pipe(shareReplay(1));
    // Emit download events
    const sub = cachedObservable.subscribe((p) => {
      this.emitProgress_(p);
      if (p.complete && p.data) {
        this.cacheWorker.postMessage({type: 'put', key: p.file.name, data: p.data});
        sub.unsubscribe();
      }
    });
    return cachedObservable;
  }

  emitProgress_(progress) {
    if (progress.complete) {
      return this.fileDownloadProgress$.next(null);
    }
    const current = progress.meta ? progress.meta.cursor || 0 : 0;
    const total = progress.file.size;
    const percent = Math.round((current * 100) / total);
    this.fileDownloadProgress$.next(Object.assign(progress,{percent}));
  }

  parseLocalFile(localFile: File): Observable<DownloadProgress> {
    const progress = {
      file: {
        name: localFile.name,
        id: localFile.name,
        size: localFile.size,
        created: firebase.firestore.Timestamp.fromMillis(localFile.lastModified),
        lastUpdate: firebase.firestore.Timestamp.fromMillis(localFile.lastModified),
        errors: []
      } as FileDocument
    } as DownloadProgress;
    const rowSet = new Set<string>();
    const progress$ = new BehaviorSubject<DownloadProgress>(progress);
    Papa.parse(localFile, {
      header: true,
      dynamicTyping: false,
      worker: true,
      skipEmptyLines: true,
      chunk: (results, parser) => {
        if (progress.file.size > FileCache.MAX_ALLOWABLE_BYTES) {
          parser.abort();
        }
        progress$.next(FileCache.processChunk_(results, progress, rowSet));
      },
      complete: () => {
        progress$.next(FileCache.completeParse_(progress));
      },
      error: (err) => {
        console.error(err);
        progress.complete = true;
        progress.success = false;
        progress$.next(progress);
      }
    });
    return this.addObservableToCache(progress$);
  }

  getFileFromStorage(ur): Observable<HttpEvent<LoadRosterSyncReview>> {

    return from(ur).pipe(switchMap((uri) => {
      const response$ = this.workerResponse.asObservable().pipe(
        take(1),
        switchMap((response) => {
          // console.log(response);
          return this.getFromStorage_(uri)
            .pipe(tap((ev) => {
              // console.log(ev);
            }));
        })
      );
      this.cacheWorker.postMessage({type: 'get', key: uri});
      return response$;
    }));
  }

  getFile(file: FileDocument, options = {refreshCache: false}): Observable<DownloadProgress> {
    if (options.refreshCache) {
      return this.getDownloadUrl(file.name).pipe(switchMap((url) => this.parseFromUrl_(url, file)));
    }
    const key = file.name;
    const response$ = this.workerResponse.asObservable().pipe(
      filter((response) => (response.key === key && response.type === 'get')),
      take(1),
      switchMap((response) => {
        if (response.data) {
          return of({
            file,
            data: response.data,
            percent: 100,
            complete: true,
            success: true
          } as DownloadProgress);
        } else {
          return this.getDownloadUrl(file.name).pipe(switchMap((url) => this.parseFromUrl_(url, file)));
        }
      })
    );
    this.cacheWorker.postMessage({type: 'get', key});
    return response$;
  }

  private parseFromUrl_(url: string, file: FileDocument): Observable<DownloadProgress> {
    if (!file.errors) {
      file.errors = [];
    }
    file.duplicateRows = {};
    const progress = {
      file
    } as DownloadProgress;
    const rowSet = new Set<string>();
    const progress$ = new BehaviorSubject<DownloadProgress>(progress);
    Papa.parse(url, {
      download: true,
      header: true,
      dynamicTyping: false,
      worker: true,
      skipEmptyLines: true,
      chunk: (results, parser) => {
        if (progress.file.size > FileCache.MAX_ALLOWABLE_BYTES) {
          parser.abort();
        }
        progress$.next(FileCache.processChunk_(results, progress, rowSet));
      },
      complete: () => {
        progress$.next(FileCache.completeParse_(progress));
      },
      error: (err) => {
        console.error(err);
        progress.complete = true;
        progress.success = false;
        progress$.next(progress);
      }
    });
    return this.addObservableToCache(progress$);
  }

  private getFromStorage_(url): Observable<HttpEvent<any>> {
    return this.http.get<RosterSyncJobReview>(url, {
      reportProgress: true,
      observe: 'events',
      responseType: 'json'
    });
  }



}
